import React from "react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import ConfirmActionDialog from "@/modules/racket_lab_admin/ConfirmActionDialog";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminShifts — Sub-sprint B.
 * List + open/close/cancel actions. No hard-delete in UI.
 *
 * QUICK 1.D i18n migration: headers, counter, columns, three action
 * dialogs (open / close / cancel) and the read-only-first subtitle
 * resolved via `racketLab.shifts.*`. Action SLUGs (`open`/`close`/
 * `cancel`) remain the backend technical contract.
 */
const RLAdminShifts = () => {
  const t = useRacketLabAdminT();
  const { data, loading, error, reload } = useAdminApi("/admin/racket-lab/shifts");
  const { run, loading: mutating } = useAdminMutation();
  const rows = Array.isArray(data) ? data : data?.items || data?.shifts || [];

  const act = (shift, action) => async () => {
    await run("post", `/admin/racket-lab/shifts/${shift.id}/${action}`, {}).catch(() => {});
    reload();
  };

  const columns = [
    { key: "id", label: t("shifts.columns.shift"), render: (r) => (
      <code data-testid={`rl-shift-id-${r.id}`} className="text-[10px]">{r.id?.slice(0, 18) || "—"}</code>
    )},
    { key: "racket_lab_shop_id", label: t("shifts.columns.shop"), render: (r) => (
      <code className="text-[10px] text-slate-500">{r.racket_lab_shop_id?.slice(0, 18) || "—"}</code>
    )},
    { key: "starts_at", label: t("shifts.columns.starts"), render: (r) => (
      <span className="text-[10px]">{r.starts_at?.slice(0, 16).replace("T", " ") || "—"}</span>
    )},
    { key: "status", label: t("shifts.columns.status"), render: (r) => (
      <span data-testid={`rl-shift-status-${r.id}`} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px]">
        {r.status || "—"}
      </span>
    )},
    { key: "actions", label: t("shifts.columns.actions"), render: (r) => (
      <div className="flex gap-1">
        {r.status === "scheduled" && (
          <ConfirmActionDialog title={t("shifts.dialog_open_title")} confirmLabel={t("shifts.action_open")} onConfirm={act(r, "open")}
            disabled={mutating} dataTestId={`rl-shift-open-${r.id}`}
            trigger={<button type="button" className="rounded bg-emerald-50 px-2 py-1 text-[10px] text-emerald-800 hover:bg-emerald-100">{t("shifts.action_open")}</button>} />
        )}
        {r.status === "open" && (
          <ConfirmActionDialog title={t("shifts.dialog_close_title")} confirmLabel={t("shifts.action_close")} onConfirm={act(r, "close")}
            disabled={mutating} dataTestId={`rl-shift-close-${r.id}`}
            trigger={<button type="button" className="rounded bg-slate-100 px-2 py-1 text-[10px] hover:bg-slate-200">{t("shifts.action_close")}</button>} />
        )}
        {["scheduled", "open"].includes(r.status) && (
          <ConfirmActionDialog title={t("shifts.dialog_cancel_title")} confirmLabel={t("shifts.action_cancel")} tone="danger"
            onConfirm={act(r, "cancel")} disabled={mutating} dataTestId={`rl-shift-cancel-${r.id}`}
            trigger={<button type="button" className="rounded bg-rose-50 px-2 py-1 text-[10px] text-rose-700 hover:bg-rose-100">{t("shifts.action_cancel")}</button>} />
        )}
      </div>
    )},
  ];

  return (
    <div data-testid="rl-admin-shifts-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-slate-900">{t("shifts.page_header")}</h1>
        <p className="text-xs text-slate-500">
          {t(rows.length === 1 ? "shifts.counter" : "shifts.counter_plural", { count: rows.length })}
        </p>
      </header>
      <p className="text-[11px] text-slate-500">{t("shifts.subtitle")}</p>
      <AdminApiTable columns={columns} rows={rows} loading={loading} error={error}
        emptyLabel={t("shifts.empty")} dataTestId="rl-shifts-table" />
    </div>
  );
};

export default RLAdminShifts;
